import React, { useState, useEffect, useReducer, useContext, forwardRef } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles(theme => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px"
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4"
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px"
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload || [];

    newTickets.forEach(ticket => {
      const ticketIndex = state.findIndex(t => Number(t.id) === Number(ticket.id));
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex(t => Number(t.id) === Number(ticketId));
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    if (!ticket || !ticket.id) return state;

    const ticketIndex = state.findIndex(t => Number(t.id) === Number(ticket.id));
    if (ticketIndex !== -1) {
      const existingTicket = state[ticketIndex];
      const mergedTicket = {
        ...existingTicket,
        ...ticket,
        user: ticket.user || existingTicket.user,
        tags: (ticket.tags && ticket.tags.length > 0) ? ticket.tags : existingTicket.tags,
        queue: ticket.queue || existingTicket.queue,
        queueOption: ticket.queueOption || existingTicket.queueOption,
        contact: { ...(existingTicket.contact || {}), ...(ticket.contact || {}) },
      };
      const nextState = [...state];
      nextState[ticketIndex] = mergedTicket;
      return nextState;
    } else {
      return [ticket, ...state];
    }
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;
    if (!ticket || !ticket.id) return state;

    const ticketIndex = state.findIndex(t => Number(t.id) === Number(ticket.id));
    if (ticketIndex !== -1) {
      const existingTicket = state[ticketIndex];
      const mergedTicket = {
        ...existingTicket,
        ...ticket,
        user: ticket.user || existingTicket.user,
        tags: (ticket.tags && ticket.tags.length > 0) ? ticket.tags : existingTicket.tags,
        queue: ticket.queue || existingTicket.queue,
        queueOption: ticket.queueOption || existingTicket.queueOption,
        contact: { ...(existingTicket.contact || {}), ...(ticket.contact || {}) },
        lastMessage: ticket.lastMessage,
        updatedAt: ticket.updatedAt,
        unreadMessages: ticket.unreadMessages !== undefined ? ticket.unreadMessages : existingTicket.unreadMessages,
      };
      const nextState = state.filter(t => Number(t.id) !== Number(ticket.id));
      nextState.unshift(mergedTicket);
      return nextState;
    } else {
      return [ticket, ...state];
    }
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex(t => Number(t.contactId) === Number(contact.id));
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "UPDATE_TICKET_PRESENCE") {
    const data = action.payload;
    const ticketIndex = state.findIndex(t => Number(t.id) === Number(data.ticketId));
    if (ticketIndex !== -1) {
      state[ticketIndex].presence = data.presence;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    if (!ticketId) return state;
    return state.filter(t => Number(t.id) !== Number(ticketId));
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = props => {
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    selectedQueueOptionIds,
    updateCount,
    style,
    hideNoTickets,
    showNewsletters,
    filterAI,
    inline,
    onScrollItems,
    sectionLabel,
    date,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [refreshTime, setRefreshTime] = useState(Date.now());
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, tags, users, selectedQueueIds, selectedQueueOptionIds, date]);

  // ── Re-sincronizar lista ao voltar do intervalo / foco na janela / aba visível ──
  useEffect(() => {
    let lastRefresh = Date.now();
    const handleReSync = () => {
      const now = Date.now();
      if (now - lastRefresh < 2000) return;
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        lastRefresh = now;
        setRefreshTime(now);
      }
    };

    window.addEventListener("focus", handleReSync);
    document.addEventListener("visibilitychange", handleReSync);

    return () => {
      window.removeEventListener("focus", handleReSync);
      document.removeEventListener("visibilitychange", handleReSync);
    };
  }, []);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    date,
    refreshKey: refreshTime,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: JSON.stringify(selectedQueueIds),
    queueOptionIds: JSON.stringify(selectedQueueOptionIds)
  });

  useEffect(() => {
    const queueIds = queues.map(q => q.id);
    const queueOptionIds = user?.queueOptions?.map(q => q.id) || [];
    const currentUserId = Number(user?.id);
    const filteredTickets = tickets.filter(
      t =>
        queueIds.indexOf(t.queueId) > -1 ||
        (t.queueOptionId && queueOptionIds.indexOf(t.queueOptionId) > -1) ||
        (t.userId && Number(t.userId) === currentUserId) ||
        (t.status === "pending" && (t.queueId === null || t.queueId === undefined))
    );

    if (profile === "user" && user?.allTicket !== "enabled") {
      dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
    } else {
      dispatch({ type: "LOAD_TICKETS", payload: tickets });
    }
  }, [tickets, status, searchParam, queues, profile, user]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const shouldUpdateTicket = ticket => {
      const currentUserId = Number(user?.id);
      const isMine = Boolean(ticket.userId && Number(ticket.userId) === currentUserId);

      if (showAll || user?.allTicket === "enabled" || user?.profile === "admin") {
        return (
          ticket.queueId === null ||
          ticket.queueId === undefined ||
          selectedQueueIds.indexOf(ticket.queueId) > -1 ||
          (selectedQueueOptionIds && selectedQueueOptionIds.length > 0 && ticket.queueOptionId && selectedQueueOptionIds.indexOf(ticket.queueOptionId) > -1) ||
          isMine
        );
      } else {
        const isPendingNoQueue = ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined);
        return (
          isMine ||
          selectedQueueIds.indexOf(ticket.queueId) > -1 ||
          (selectedQueueOptionIds && selectedQueueOptionIds.length > 0 && ticket.queueOptionId && selectedQueueOptionIds.indexOf(ticket.queueOptionId) > -1) ||
          ((selectedQueueIds.length === 0 || selectedQueueIds.length === queues.length) && isPendingNoQueue)
        );
      }
    };

    const onReady = () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
      setRefreshTime(Date.now());
    };

    onReady();
    socket.on("ready", onReady);

    const onTicketEvent = data => {
      if (!data) return;

      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId
        });
      }

      const ticketId = data.ticketId || data.ticket?.id;

      if (data.action === "delete") {
        if (ticketId) {
          dispatch({ type: "DELETE_TICKET", payload: ticketId });
        }
      }

      if (data.action === "update" && data.ticket) {
        if (status !== undefined && data.ticket.status !== status) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
        } else if (!shouldUpdateTicket(data.ticket)) {
          dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
        } else if (status === undefined || data.ticket.status === status) {
          dispatch({
            type: "UPDATE_TICKET",
            payload: data.ticket
          });
        }
      }
    };

    const onAppMessageEvent = data => {
      // Ignorar eventos de fora do expediente (sem ticket para atualizar)
      if (data.action === "outOfHours") return;
      if (!data.ticket) return;

      // Se a mensagem pertence a um ticket cujo status é diferente desta lista, remover!
      if (status !== undefined && data.ticket.status && data.ticket.status !== status) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
        return;
      }

      const queueIds = queues.map(q => q.id);
      const userQueueOptionIds = user?.queueOptions?.map(q => q.id) || [];
      const isInUserQueue = queueIds.indexOf(data.ticket.queueId) > -1;
      const isInUserQueueOption = data.ticket.queueOptionId && userQueueOptionIds.indexOf(data.ticket.queueOptionId) > -1;
      const isAssignedToMe = data.ticket.userId && Number(data.ticket.userId) === Number(user?.id);
      const isPendingNoQueue = data.ticket.status === "pending" && (data.ticket.queueId === null || data.ticket.queueId === undefined);
      const isNotUserQueue = !isInUserQueue && !isInUserQueueOption && !isAssignedToMe && !isPendingNoQueue;

      if (
        profile === "user" && user?.allTicket !== "enabled" && isNotUserQueue
      ) {
        return;
      }

      if (
        data.action === "create" &&
        shouldUpdateTicket(data.ticket) &&
        (status === undefined || data.ticket.status === status)
      ) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket
        });
      }

      // ── Atualizar ticket na lista quando chega nova mensagem (action update) ──
      if (
        data.action === "update" &&
        data.ticket &&
        shouldUpdateTicket(data.ticket) &&
        (status === undefined || data.ticket.status === status)
      ) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket
        });
      }
    };

    const onPresenceEvent = data => {
      dispatch({
        type: "UPDATE_TICKET_PRESENCE",
        payload: data
      });
    };

    const onContactEvent = data => {
      if (data?.action === "update" && data?.contact?.id) {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact
        });
      }
    };

    socket.on(`company-${companyId}-ticket`, onTicketEvent);
    socket.on(`company-${companyId}-appMessage`, onAppMessageEvent);
    socket.on(`company-${companyId}-presence`, onPresenceEvent);
    socket.on(`company-${companyId}-contact`, onContactEvent);

    return () => {
      socket.off("ready", onReady);
      socket.off(`company-${companyId}-ticket`, onTicketEvent);
      socket.off(`company-${companyId}-appMessage`, onAppMessageEvent);
      socket.off(`company-${companyId}-presence`, onPresenceEvent);
      socket.off(`company-${companyId}-contact`, onContactEvent);
    };
  }, [
    status,
    showAll,
    user,
    selectedQueueIds,
    selectedQueueOptionIds,
    tags,
    users,
    profile,
    queues,
    socketManager
  ]);

  let filteredList = ticketsList.filter(ticket => {
    if (status && ticket.status !== status) return false;
    if (ticket.isGroup.toString() !== "false") return false;
    if (!showNewsletters && ticket.contact?.isNewsletter) return false;
    return true;
  });

  // Filtrar apenas tickets atendidos por IA
  if (filterAI) {
    filteredList = filteredList.filter(ticket => ticket.useIntegration && ticket.promptId && !ticket.userId);
  }

  const isEmptyList = filteredList.length === 0 && !loading;

  useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(filteredList.length);
    }
  }, [filteredList.length, updateCount]);

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // Inline mode: render items directly without wrappers (for unified scroll)
  if (inline) {
    if (hideNoTickets && isEmptyList) return null;

    return (
      <>
        {sectionLabel && filteredList.length > 0 && (
          <div style={{
            padding: "6px 16px 4px",
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            color: "rgba(255,255,255,0.4)",
          }}>
            {sectionLabel}
          </div>
        )}
        {filteredList.length === 0 && !loading && !hideNoTickets ? (
          <div className={classes.noTicketsDiv}>
            <span className={classes.noTicketsTitle}>
              {i18n.t("ticketsList.noTicketsTitle")}
            </span>
            <p className={classes.noTicketsText}>
              {i18n.t("ticketsList.noTicketsMessage")}
            </p>
          </div>
        ) : (
          filteredList.map(ticket => (
            <TicketListItem ticket={ticket} key={ticket.id} />
          ))
        )}
        {loading && <TicketsListSkeleton />}
      </>
    );
  }

  return (
    <Paper className={classes.ticketsListWrapper} style={{
      ...style,
      ...(hideNoTickets && isEmptyList ? { display: "none" } : {}),
    }}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading && !hideNoTickets ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {filteredList
                .map(ticket => (
                  <TicketListItem ticket={ticket} key={ticket.id} />
                ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
